import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, LogOut, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { NAV, ROLE_LABELS, roleOf } from "@/lib/constants";

const SideNav = ({ items, onNavigate }) => (
  <div className="flex h-full flex-col bg-slate-950 text-slate-100">
    <div className="flex items-center gap-2 px-6 h-16 border-b border-white/5">
      <span className="grid place-items-center h-8 w-8 rounded-lg bg-teal-600"><Activity className="h-4 w-4" /></span>
      <span className="font-display text-lg font-semibold tracking-tight">Klinic</span>
    </div>
    <nav className="flex-1 space-y-1 p-3">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} onClick={onNavigate} data-testid={`nav-${it.to.slice(1)}-link`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-teal-600/20 text-teal-200" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
          <it.icon className="h-4 w-4" />
          <span className="flex-1">{it.label}</span>
          {it.soon && <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-300">Soon</span>}
        </NavLink>
      ))}
    </nav>
    <p className="px-6 py-4 text-[11px] text-slate-500">Outpatient OS · v1</p>
  </div>
);

const ClinicPicker = () => {
  const { user, clinics, activeClinicId, setActiveClinicId, activeClinic } = useAuth();
  if (!clinics.length) return null;
  if (user.role !== "ceo") return <span className="hidden sm:inline text-sm font-semibold text-slate-700" data-testid="header-clinic-name">{activeClinic?.name}</span>;
  return (
    <Select value={activeClinicId} onValueChange={setActiveClinicId}>
      <SelectTrigger className="w-44 sm:w-56 rounded-full" data-testid="clinic-selector-dropdown"><SelectValue /></SelectTrigger>
      <SelectContent>{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export default function AppShell() {
  const { user, logout, activeClinic } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const role = roleOf(user);
  const items = NAV[role] || [];
  const out = async () => { await logout(); nav("/login"); };
  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden lg:block w-64 shrink-0"><SideNav items={items} /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 border-0"><SheetTitle className="sr-only">Menu</SheetTitle><SideNav items={items} onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <div className="flex flex-1 flex-col min-w-0">
        <header className="no-print sticky top-0 z-20 flex h-16 items-center gap-3 bg-white/80 backdrop-blur-xl border-b border-slate-200/80 px-4 md:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
          <ClinicPicker />
          {activeClinic && <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-mono text-xs font-semibold text-emerald-700" data-testid="header-currency-badge">{activeClinic.currency}</span>}
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-right leading-tight">
              <p className="text-sm font-semibold text-slate-800" data-testid="header-user-name">{user.name}</p>
              <p className="text-xs text-teal-700" data-testid="header-role-badge">{ROLE_LABELS[role]}</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={out} data-testid="logout-button"><LogOut className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Sign out</span></Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto grain">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-10 space-y-8"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
